import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { appCrashesApi, type AppCrashGroup } from '@/api/appCrashes';
import { PageHeader } from '@/components/ui/PageHeader';
import { humanizeError } from '@/lib/errors';

import { CRASH_KIND_KEY, mayBeIOS26, systemLabel, versionLabel } from './appCrashesView';

/**
 * «Падения приложения» (T23, docs/tasks/T23-crash-reports.md): что падало у учеников в приложении 1.1.2+.
 * Сверху — свод по версии и системе, ниже — последние 100 групп со стеком.
 *
 * ⚠️ ОТЧЁТЫ АНОНИМНЫЕ (решение владельца 17.10.2026): здесь нет и не будет имени ученика — по отчёту
 * видно, ЧТО упало, но не У КОГО. Хранятся 30 дней.
 *
 * Без отдельной телефонной ветки: строки с переносами одинаково работают на 390 и 1440.
 */
export function AdminAppCrashesPage() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['admin', 'app-crashes'], queryFn: appCrashesApi.overview, refetchInterval: 60_000 });
  const d = q.data;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title={t('adminAppCrashes.title')} subtitle={t('adminAppCrashes.subtitle')} />

      {q.isError && (
        <p className="mb-4 text-sm text-error">
          {t('adminAppCrashes.loadError')} {humanizeError(q.error)}
        </p>
      )}

      <section className="rounded-[20px] bg-surface-container px-[18px] py-4">
        <h2 className="mb-3 font-display text-[14.5px] font-bold text-on-surface">{t('adminAppCrashes.summaryTitle')}</h2>
        {d && d.summary.length === 0 && (
          <p className="py-3 text-center text-[12.5px] text-on-surface-variant">{t('adminAppCrashes.empty')}</p>
        )}
        <ul className="divide-y divide-outline">
          {(d?.summary ?? []).map((r) => (
            <li
              key={`${r.app_version}|${r.platform}|${r.os_version}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2 text-[13px]"
            >
              <span className="text-on-surface">
                {versionLabel(r.app_version, '') || t('adminAppCrashes.noVersion')}
                {' · '}
                {systemLabel(r.platform, r.os_version) || t('adminAppCrashes.noSystem')}
                {mayBeIOS26(r.platform, r.os_version) && (
                  <span className="ml-1 text-[11.5px] text-on-surface-variant">({t('adminAppCrashes.ios26Note')})</span>
                )}
              </span>
              <span className="tabular-nums text-on-surface-variant">
                {t('adminAppCrashes.times', { count: r.count })} · {t('adminAppCrashes.kinds', { count: r.groups })}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-3.5 rounded-[20px] bg-surface-container px-[18px] py-4">
        <h2 className="mb-3 font-display text-[14.5px] font-bold text-on-surface">{t('adminAppCrashes.groupsTitle')}</h2>
        {d && d.groups.length === 0 && (
          <p className="py-3 text-center text-[12.5px] text-on-surface-variant">{t('adminAppCrashes.empty')}</p>
        )}
        <ul className="divide-y divide-outline">
          {(d?.groups ?? []).map((g) => (
            <CrashRow key={g.id} g={g} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function CrashRow({ g }: { g: AppCrashGroup }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const last = new Date(g.last_seen).toLocaleString(i18n.language, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="rounded-full bg-error-container px-2 py-0.5 text-[11px] font-semibold text-on-error-container">
          {t(CRASH_KIND_KEY[g.kind] ?? 'adminAppCrashes.kindJs')}
        </span>
        <span className="min-w-0 flex-1 break-words text-[13px] text-on-surface">
          {g.message || t('adminAppCrashes.noMessage')}
        </span>
      </div>
      <p className="mt-1 text-[12px] text-on-surface-variant">
        {[
          versionLabel(g.app_version, g.app_build) || t('adminAppCrashes.noVersion'),
          systemLabel(g.platform, g.os_version) || t('adminAppCrashes.noSystem'),
          t('adminAppCrashes.times', { count: g.count }),
          t('adminAppCrashes.lastSeen', { at: last }),
        ].join(' · ')}
      </p>
      {g.route && <p className="mt-0.5 break-all text-[12px] text-on-surface-variant">{t('adminAppCrashes.route', { route: g.route })}</p>}
      {g.stack && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1 text-[12px] font-semibold text-primary"
          >
            {open ? t('adminAppCrashes.hideStack') : t('adminAppCrashes.showStack')}
          </button>
          {open && (
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-surface-container-high p-2 text-[11px] text-on-surface">
              {g.stack}
            </pre>
          )}
        </>
      )}
    </li>
  );
}
